import React, { useEffect, useState } from 'react';
import type { Address } from '../../types';
import { loadClientCompany, saveClientCompanyBillingAddress } from '../../utils/clientCompanies';
import './CompanyBillingAddressFields.css';

const blank = (): Address => ({
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'United States',
});

interface CompanyBillingAddressFieldsProps {
  companyName: string;
}

const CompanyBillingAddressFields: React.FC<CompanyBillingAddressFieldsProps> = ({ companyName }) => {
  const name = companyName.trim();
  const [address, setAddress] = useState<Address>(blank);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!name) return;
    let cancelled = false;
    setLoading(true);
    setSaved(false);
    setError(null);
    loadClientCompany(name)
      .then((record) => {
        if (cancelled) return;
        setAddress(record?.billingAddress ? { ...blank(), ...record.billingAddress } : blank());
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the company billing address.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [name]);

  if (!name) return null;

  const setField = (field: keyof Address, value: string) => {
    setSaved(false);
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await saveClientCompanyBillingAddress(name, address);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the billing address.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cba-root">
      <div className="cba-head">
        <span className="cba-label">Billing address</span>
        <span className="cba-company">{name}</span>
      </div>
      <p className="cba-hint">
        QuickBooks invoices for this company use this address. It is shared by every project for {name}.
      </p>
      <input
        className="cba-input"
        value={address.street}
        disabled={loading || saving}
        placeholder="Street"
        aria-label="Billing street"
        onChange={(e) => setField('street', e.target.value)}
      />
      <div className="cba-city-row">
        <input
          className="cba-input"
          value={address.city}
          disabled={loading || saving}
          placeholder="City"
          aria-label="Billing city"
          onChange={(e) => setField('city', e.target.value)}
        />
        <input
          className="cba-input cba-state"
          value={address.state}
          disabled={loading || saving}
          placeholder="State"
          aria-label="Billing state"
          onChange={(e) => setField('state', e.target.value)}
        />
        <input
          className="cba-input cba-zip"
          value={address.zipCode}
          disabled={loading || saving}
          placeholder="ZIP"
          aria-label="Billing ZIP"
          onChange={(e) => setField('zipCode', e.target.value)}
        />
      </div>
      <div className="cba-actions">
        <button type="button" className="cba-save" onClick={() => void save()} disabled={loading || saving}>
          {saving ? 'Saving…' : 'Save billing address'}
        </button>
        {saved && <span className="cba-saved">Saved</span>}
        {error && <span className="cba-error">{error}</span>}
      </div>
    </div>
  );
};

export default CompanyBillingAddressFields;
